import { createFileRoute } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { isAllowedActivateNext } from "@/lib/activate-link";
import { z } from "zod";

const searchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/activate")({
  validateSearch: (s) => searchSchema.parse(s),
  component: ActivatePage,
  head: () => ({
    meta: [
      { title: "Continue — Bracket Goblin" },
      // Discourage link previewers / scanners from following further.
      { name: "robots", content: "noindex, nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
});

function ActivatePage() {
  const { next } = Route.useSearch();
  const valid = !!next && isAllowedActivateNext(next);
  const [going, setGoing] = useState(false);

  const handleContinue = () => {
    if (!valid || !next) return;
    setGoing(true);
    // Hard navigation to the Supabase verify URL. Scanners can't execute
    // this JS, so the one-time token stays unspent until a human clicks.
    window.location.href = next;
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-neon)]">
          <Trophy className="h-6 w-6" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight text-foreground mb-2">
          One more tap
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Click the button below to continue. We do this extra step so that
          email scanners (Outlook, Gmail, corporate filters) don&apos;t
          accidentally use up your one-time link before you do.
        </p>
        {valid ? (
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={handleContinue}
            disabled={going}
          >
            {going ? "Continuing…" : "Continue"}
          </Button>
        ) : (
          <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            This link is missing or malformed. Please return to your email and
            click the button again, or request a fresh link from the sign-in
            page.
          </div>
        )}
      </div>
    </main>
  );
}
